import React, { useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { QRCodeSVG } from 'qrcode.react';

export default function RoomQRCodeModal({ open, onClose, room, tenantId }) {
    const qrRef = useRef(null);

    if (!room) return null;

    // Build the guest portal URL for this room.
    const currentHost = window.location.host;
    const isLocalhost = currentHost.includes('localhost');
    const guestHost = isLocalhost ? 'guest.localhost:3000' : currentHost.replace(/^(admin|pms)/, 'guest');
    const protocol = window.location.protocol;
    
    // The token format expected by the backend is 'r_' + roomId
    const roomToken = `r_${room.id}`;
    const qrUrl = `${protocol}//${guestHost}/verify?token=${roomToken}&tenantId=${tenantId || 1}`;

    const handlePrint = () => {
        const printContent = qrRef.current.innerHTML;
        const originalContent = document.body.innerHTML;
        const printWindow = window.open('', '', 'height=600,width=800');
        
        printWindow.document.write('<html><head><title>Print QR Code</title>');
        printWindow.document.write('<style>body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; text-align: center; } .qr-container { padding: 40px; border: 2px solid #ccc; border-radius: 16px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); } h2 { margin-top: 0; color: #333; } p { color: #666; margin-bottom: 30px; }</style>');
        printWindow.document.write('</head><body >');
        printWindow.document.write('<div class="qr-container">');
        printWindow.document.write(`<h2>Room ${room.roomNumber || room.number}</h2>`);
        printWindow.document.write('<p>Scan to access Room Service & Guest Portal</p>');
        printWindow.document.write(printContent);
        printWindow.document.write('</div>');
        printWindow.document.write('</body></html>');
        
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    const handleDownload = () => {
        const svg = qrRef.current.querySelector('svg');
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('react'); // it's 2d context actually
        const ctx2d = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            canvas.width = img.width + 100;
            canvas.height = img.height + 150;
            
            // White background
            ctx2d.fillStyle = 'white';
            ctx2d.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add text
            ctx2d.fillStyle = 'black';
            ctx2d.font = 'bold 24px Arial';
            ctx2d.textAlign = 'center';
            ctx2d.fillText(`Room ${room.roomNumber || room.number}`, canvas.width / 2, 40);
            
            ctx2d.font = '16px Arial';
            ctx2d.fillStyle = '#666';
            ctx2d.fillText('Scan for Room Service', canvas.width / 2, 70);
            
            // Draw QR code
            ctx2d.drawImage(img, 50, 100);
            
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `Room_${room.roomNumber || room.number}_QRCode.png`;
            downloadLink.href = `${pngFile}`;
            downloadLink.click();
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Room QR Code - {room.roomNumber || room.number}</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <Box ref={qrRef} sx={{ p: 3, bgcolor: 'white', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <QRCodeSVG 
                        value={qrUrl} 
                        size={250} 
                        level="H"
                        includeMargin={true}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    Guests can scan this code to verify their identity and access the digital guest portal for Room {room.roomNumber || room.number}.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
                <Button 
                    variant="outlined" 
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                    onClick={handleDownload}
                >
                    Download PNG
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<PrintIcon />} 
                    onClick={handlePrint}
                >
                    Print QR Code
                </Button>
            </DialogActions>
        </Dialog>
    );
}
